import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, TrendingUp, Target, Users } from 'lucide-react';
import { ABOUT_PAGE } from '../content';

const About = () => {
  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 via-white to-pink-50 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              {ABOUT_PAGE.hero.title} <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">{ABOUT_PAGE.hero.titleHighlight}</span>
            </h1>
            <p className="text-xl text-gray-600">
              {ABOUT_PAGE.hero.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">{ABOUT_PAGE.story.title}</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                {ABOUT_PAGE.story.paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1709715357520-5e1047a2b691?w=800&q=80"
                  alt="Team meeting"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Achievements */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{ABOUT_PAGE.achievements.title}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {ABOUT_PAGE.achievements.description}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {ABOUT_PAGE.achievements.items.map((item, index) => {
              const icons = [TrendingUp, Target, MapPin];
              const IconComponent = icons[index];
              const gradients = [
                'from-green-500 to-emerald-600',
                'from-pink-500 to-pink-600',
                'from-emerald-500 to-green-600'
              ];
              return (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className={`w-16 h-16 bg-gradient-to-br ${gradients[index]} rounded-xl flex items-center justify-center mb-6`}>
                    <IconComponent className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=800&q=80"
                  alt="Professional teamwork"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
            <div className="space-y-6 order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-gray-900">{ABOUT_PAGE.vision.title}</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                {ABOUT_PAGE.vision.paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
              <Link to="/careers" className="flex items-center space-x-4 pt-4 group w-fit">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Users className="text-green-600" size={28} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{ABOUT_PAGE.vision.badge}</p>
                  <p className="text-lg font-semibold text-gray-900 group-hover:text-green-600 transition-colors">{ABOUT_PAGE.vision.badgeDescription}</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;